import { useEffect, useState } from "react";
import type { Game } from "../types/game";
import {
  getGenres,
  PLATFORM_OPTIONS,
  searchGames,
  type GenreOption,
} from "../lib/api";
import GameCard from "../components/GameCard";
import GameDetail from "../components/GameDetail";

export default function Browse() {
  const [query, setQuery] = useState("");
  const [genreId, setGenreId] = useState("");
  const [platformId, setPlatformId] = useState("");
  const [genres, setGenres] = useState<GenreOption[]>([]);

  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Game | null>(null);

  // Load genre list + an initial set of popular games once.
  useEffect(() => {
    getGenres().then(setGenres).catch(() => setGenres([]));
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function run(e?: React.FormEvent) {
    e?.preventDefault();
    setLoading(true);
    setError(null);
    setSelected(null);
    try {
      const results = await searchGames({
        query,
        genreId: genreId || undefined,
        platformId: platformId || undefined,
      });
      setGames(results);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  if (selected) {
    return <GameDetail game={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <section>
      <h1 style={{ marginTop: 0 }}>Find games to play together</h1>
      <p style={{ color: "#b3b9c5", marginTop: 0 }}>
        Search the catalog, then open a game to see its co-op and crossplay
        details.
      </p>

      <form className="filters" onSubmit={run}>
        <input
          type="search"
          placeholder="Search games…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select value={platformId} onChange={(e) => setPlatformId(e.target.value)}>
          <option value="">All platforms</option>
          {PLATFORM_OPTIONS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select value={genreId} onChange={(e) => setGenreId(e.target.value)}>
          <option value="">All genres</option>
          {genres.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
        <button type="submit" disabled={loading}>
          {loading ? "Searching…" : "Search"}
        </button>
      </form>

      {error && <div className="status-error">⚠ {error}</div>}
      {loading && <div className="status-loading">Loading games…</div>}

      {!loading && !error && games.length === 0 && (
        <p style={{ color: "#9098a8" }}>No games found. Try another search.</p>
      )}

      <div className="grid">
        {games.map((g) => (
          <GameCard key={g.id} game={g} onClick={() => setSelected(g)} />
        ))}
      </div>
    </section>
  );
}
